// Campaign Types and Enums

export enum Platform {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  YELP = 'yelp',
  TRUSTPILOT = 'trustpilot',
  TRIPADVISOR = 'tripadvisor',
}

export enum RewardType {
  NONE = 'none',
  COUPON = 'coupon',
  CASH = 'cash',
}

export enum PaymentIntegrator {
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
  SQUARE = 'square',
  RAZORPAY = 'razorpay',
  MANUAL = 'manual',
}

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  CAD = 'CAD',
  AUD = 'AUD',
  INR = 'INR',
}

// Base campaign fields
export interface BaseCampaignRequest {
  campaign_name: string;
  platform: Platform | string;
  platform_link: string;
  reward_type: RewardType | string;
}

// Campaign with no rewards
export interface NoRewardCampaignRequest extends BaseCampaignRequest {
  reward_type: 'none';
}

// Campaign with coupon rewards
export interface CouponCampaignRequest extends BaseCampaignRequest {
  reward_type: 'coupon';
  coupon_discount_percent: number;
  coupon_expiry_date: string; // ISO 8601 format
  coupon_terms: string;
  coupon_prefix: string;
}

// Campaign with cash rewards
export interface CashCampaignRequest extends BaseCampaignRequest {
  reward_type: 'cash';
  cash_amount: number;
  cash_currency: Currency | string;
  payment_integrator: PaymentIntegrator | string;
}

// Union type for all campaign requests
export type CreateCampaignRequest = 
  | NoRewardCampaignRequest 
  | CouponCampaignRequest 
  | CashCampaignRequest;

// Survey Question structure
export interface SurveyQuestion {
  id: string;
  question_text: string;
  answer_type: 'text' | 'rating' | 'multiple_choice' | 'yes_no' | 'number';
  options?: string[];
  is_mandatory: boolean;
  question_order: number;
}

// Campaign data structure (response)
export interface CampaignData {
  id: string;
  campaign_name: string;
  platform: string;
  platform_link: string;
  reward_type: string;
  
  // Coupon fields (optional)
  coupon_discount_percent?: number;
  coupon_expiry_date?: string;
  coupon_terms?: string;
  coupon_prefix?: string;
  
  // Cash fields (optional)
  cash_amount?: number;
  cash_currency?: string;
  payment_integrator?: string;
  
  // Survey questions (optional)
  survey?: {
    id: string;
    title: string;
    description?: string;
    questions: SurveyQuestion[];
  };
  
  // Campaign stats
  total_contacts?: number;
  total_responses?: number;
  response_rate?: number;
  
  // Metadata
  created_at?: string;
  updated_at?: string;
  status?: string;
}

export interface CreateCampaignResponse {
  success: boolean;
  data?: {
    message: string;
    campaign: CampaignData;
  };
  message?: string;
}

export interface GetCampaignsResponse {
  success: boolean;
  data?: {
    campaigns: CampaignData[];
    total: number;
  };
  message?: string;
}

export interface CampaignStats {
  campaign_id: string;
  total_contacts: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  responded_count: number;
  skipped_count: number;
  total_batches: number;
  sent_rate: number;
  delivery_rate: number;
  open_rate: number;
  response_rate: number;
}

export interface GetCampaignStatsResponse {
  success: boolean;
  data?: {
    stats: CampaignStats;
  };
  message?: string;
}

// Helper type for form data
export interface CampaignFormData {
  campaignName: string;
  platform: Platform | string;
  platformLink: string;
  rewardType: RewardType | string;
  
  // Coupon fields
  couponDiscountPercent?: number;
  couponExpiryDate?: string;
  couponTerms?: string;
  couponPrefix?: string;
  
  // Cash fields
  cashAmount?: number;
  cashCurrency?: Currency | string;
  paymentIntegrator?: PaymentIntegrator | string;
}
